<template>
  <div
    @click.right.stop
    @click.stop
    @dragstart="isDragging = true"
    @dragend="onDragEnd"
    class="file-preview-list"
    :class="{ 'translate-x-full transition-transform': isDragging }"
  >
    <div
      :class="[
        !minimal ? 'border-gray-lighten-1 surface absolute! right-0 rounded-md border-2 p-2' : '',
        containerClass,
        dynamicContainerClass
      ]"
      @mouseleave.stop="$emit('leave')"
    >
      <div v-if="!minimal" class="mb-2 flex w-full justify-end sm:hidden">
        <GeneralButton variant="outlined" size="small" color="warning" @click="$emit('close')">
          Annuleren
        </GeneralButton>
      </div>

      <div v-if="$slots.default" class="mb-2 flex w-full text-xs font-bold select-none">
        <slot />
      </div>

      <div
        class="grid auto-rows-fr gap-1"
        :class="[gridClass, dynamicGridClass]"
        v-if="renderComponent"
      >
        <FilePreviewTile
          v-for="(fileRelation, i) in fileRelations"
          :key="fileRelation.id"
          v-model="fileRelations[i]"
          @click="onClickTile(fileRelation)"
          @reload="forceRender"
          @delete="promptDeleteFileRelation"
          @moved="onMove?.(fileRelation)"
          @context-menu-state-change="onContextMenuStateChange"
          :drag-default-is-copy="dragDefaultIsCopy"
          :allow-context-menu="allowContextMenu"
          :delete-disabled-reason="deleteDisabledReason?.(fileRelation)"
          class="h-40"
        />
        <FilePreviewTile v-if="isUploading" class="h-40" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, nextTick, ref, watch, computed } from "vue"
import { FileRelation } from "@/models/file/file-relation.model"
import { FileViewerController } from "@/controllers/file-viewer-controller"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import FilePreviewTile from "@/components/file/FilePreviewTile.vue"
import type { FileController } from "@/controllers/file-controller"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"

const props = withDefaults(
  defineProps<{
    minimal?: boolean
    containerClass?: string
    gridClass?: string
    isUploading?: boolean
    dragDefaultIsCopy?: boolean
    closeOnClick?: boolean
    onDelete?: (fileRelation: FileRelation) => void
    deleteDisabledReason?: (fileRelation: FileRelation) => string | undefined
    onMove?: (fileRelation: FileRelation) => void
    allowContextMenu?: boolean
  }>(),
  {
    minimal: false,
    containerClass: "",
    gridClass: "h-full",
    isUploading: false,
    closeOnClick: true,
    allowContextMenu: true
  }
)

const emit = defineEmits(["close", "allImagesLoaded", "open", "leave"])

const fileRelations = defineModel<FileRelation[]>({ required: true })

const isDragging = ref(false)

function onClickTile(fileRelation: FileRelation) {
  FileViewerController.display(fileRelations.value, fileRelation, {
    getArray: () => fileRelations.value,
    onDelete: props.onDelete,
    deleteDisabledReason: props.deleteDisabledReason
  })
  if (props.closeOnClick) {
    emit("close")
  }
}

const fileController: FileController = inject("fileController")!
function promptDeleteFileRelation(fileRelation: FileRelation) {
  if (props.deleteDisabledReason?.(fileRelation)) return
  const dialogStore = useDialogStore()
  dialogStore.currentDialog = {
    title: "Bestand verwijderen",
    body: `Weet je zeker dat je ${fileRelation.file.originalName} wilt verwijderen?`,
    type: DialogTypes.CONFIRM,
    onConfirm: () => deleteFileRelation(fileRelation),
    textConfirm: "Verwijderen",
    classConfirm: "bg-error! text-on-error!"
  }
}

async function deleteFileRelation(fileRelation: FileRelation) {
  const reason = props.deleteDisabledReason?.(fileRelation)
  if (reason) return
  try {
    await fileController.deleteFileRelation(fileRelation, props.onDelete)
  } catch {
    useDialogStore().currentDialog = {
      title: "Bestand niet verwijderd",
      body: "Het bestand kon niet worden verwijderd. Controleer of de akkoordkaart nog bewerkbaar is en probeer opnieuw.",
      type: DialogTypes.OK
    }
    return
  }

  // Delete from local list (necessary for unsaved files, i.e. empty instruction processing row).
  const index = fileRelations.value.findIndex((fr) => fr.id === fileRelation.id)
  if (index !== -1) {
    fileRelations.value.splice(index, 1)
  }
}

// close menu when file lenght is 0
watch(
  () => fileRelations.value.length,
  (newLength) => {
    if (newLength === 0) {
      emit("close")
    }
  }
)

const renderComponent = ref(true)
/* This can be called to re-mount the component, for example then the image is rotated and the preview needs to be updated  */
function forceRender() {
  renderComponent.value = false
  nextTick(() => {
    renderComponent.value = true
  })
}

const dynamicGridClass = computed(() => {
  const itemCount = fileRelations.value.length + (props.isUploading ? 1 : 0)
  if (itemCount === 1) return "grid-cols-1"
  if (itemCount === 2) return "grid-cols-2"
  if (itemCount === 3) return "grid-cols-3"
  return "grid-cols-3" // Default to 3 columns for more than 3 items
})

const dynamicContainerClass = computed(() => {
  const itemCount = fileRelations.value.length + (props.isUploading ? 1 : 0)
  if (itemCount === 1) return "w-1/3 max-w-48"
  if (itemCount === 2) return "w-2/3 max-w-96"
  if (itemCount === 3) return "w-full  max-w-xl"
  return "w-full" // Default to full width for more than 3 items
})

function onDragEnd() {
  isDragging.value = false
  emit("close")
}

function onContextMenuStateChange(isOpen: boolean) {
  emit("open", isOpen)
}
</script>

<style scoped></style>
